import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Box, Button, Field, Show, Text, VStack } from '@chakra-ui/react';

import isEqual from 'lodash/isEqual';

import { FormConsentsProps } from './form-consents.props';
import { CheckedChangeDetails } from 'node_modules/@chakra-ui/react/dist/types/components/checkbox/namespace';

import { useTranslation } from '../../../hooks/use-translation';
import {
    AdministratorData,
    ConsentDataModel,
    ConsentNameType,
    getDefaultFormConsentsTranslation,
} from '../../../models';
import { Checkbox, CheckedStatus } from '../../atoms';

export const FormConsents = forwardRef<HTMLDivElement, FormConsentsProps>(
    (
        {
            urls,
            consents,
            initiallySelectedConsents,
            translationData = getDefaultFormConsentsTranslation({
                administratorData: AdministratorData.SzallasGroup,
            }),
            translationKey,
            onValuesChanged,
            onCheckboxChanged,
            hideAdministratorData = false,
            disabled,
            ...props
        },
        ref,
    ): JSX.Element => {
        const { setValue, getValues, watch, trigger, getFieldState } = useFormContext();
        const { t } = useTranslation(translationKey);
        const [showAdministratorData, setShowAdministratorData] = useState(false);
        const [expandedItems, setExpandedItems] = useState(new Array(consents.length).fill(false));
        const [checkedItems, setCheckedItems] = useState<CheckedStatus[]>(
            new Array(consents.length).fill(false),
        );
        const checkboxesState = useMemo(() => {
            if (checkedItems.every(item => item === true)) {
                return true;
            } else if (checkedItems.every(item => item === false)) {
                return false;
            }

            return 'indeterminate';
        }, [checkedItems]);

        const isSimplified = consents.length <= 1;

        const formData = watch();

        useEffect(() => {
            if (!onValuesChanged) {
                return;
            }

            onValuesChanged(formData);
        }, [formData]);

        useEffect(() => {
            if (!initiallySelectedConsents) return;

            const currentCheckboxes = Object.values(initiallySelectedConsents);
            if (isEqual(currentCheckboxes, checkedItems)) return;

            const checkboxes = getValues('consentCheckbox');
            if (!checkboxes) return;

            Object.keys(checkboxes).forEach(key => {
                const currentVal = initiallySelectedConsents[key as ConsentNameType];

                setValue(`consentCheckbox[${key}]`, currentVal, {
                    shouldValidate: true,
                });
            });

            setCheckedItems([...currentCheckboxes]);
        }, [initiallySelectedConsents]);

        const toggleExpandedTextVisibility = useCallback(
            (e: React.MouseEvent<HTMLElement>, index: number) => {
                e.preventDefault();
                const items = [...expandedItems];
                items[index] = !expandedItems[index];
                setExpandedItems(items);
            },
            [expandedItems],
        );

        const handleOnChange = useCallback(
            (checked: CheckedStatus, id: ConsentNameType, index: number) => {
                const items = [...checkedItems];
                items[index] = checked;
                setCheckedItems([...items]);

                if (onCheckboxChanged) {
                    onCheckboxChanged();
                }
            },
            [checkedItems, onCheckboxChanged],
        );

        const handleSelectAll = useCallback(
            (checked: CheckedStatus) => {
                const checkboxes = getValues('consentCheckbox');

                Object.keys(checkboxes).forEach(index => {
                    setValue(`consentCheckbox.${index}`, checked, {
                        shouldValidate: true,
                    });
                });

                setCheckedItems(new Array(consents.length).fill(checked));
            },
            [consents.length, getValues, setValue],
        );

        return (
            <Box py={3} borderRadius={'md'} ref={ref} {...props}>
                {isSimplified ? null : (
                    <Checkbox
                        checked={checkboxesState}
                        disabled={disabled}
                        onCheckedChange={(details: CheckedChangeDetails) =>
                            handleSelectAll(details.checked)
                        }
                        mb={2}
                    >
                        <Text as="span" fontSize="sm">
                            {translationData?.selectAll}
                        </Text>
                    </Checkbox>
                )}

                <VStack pl={isSimplified ? 0 : 4} mt={isSimplified ? 0 : 3} gap={3}>
                    {consents?.map(
                        (
                            {
                                id,
                                isRequired,
                                excerptTranslationKey,
                                expandedTextTranslationKey,
                                validationMessage,
                                smEmailOptIn,
                            }: ConsentDataModel,
                            index: number,
                        ) => {
                            const { invalid } = getFieldState(`consentCheckbox.${id}`);

                            return (
                                <Controller
                                    defaultValue={false}
                                    key={id}
                                    name={`consentCheckbox.${id}`}
                                    rules={{
                                        required: isRequired,
                                    }}
                                    render={({ field }) => (
                                        <Field.Root
                                            invalid={invalid}
                                            disabled={disabled}
                                            required={isRequired}
                                        >
                                            <Checkbox
                                                data-testid={`consent-${id}`}
                                                data-sm-email-opt-in={smEmailOptIn}
                                                checked={field.value}
                                                onCheckedChange={({ checked }) => {
                                                    field.onChange(checked);
                                                    trigger(`consentCheckbox.${id}`);
                                                    handleOnChange(checked, id, index);
                                                }}
                                                alignItems={'start'}
                                                lineHeight={1}
                                            >
                                                <Text
                                                    as="span"
                                                    fontSize="xs"
                                                    dangerouslySetInnerHTML={{
                                                        __html: `${t(excerptTranslationKey, urls || {})}`,
                                                    }}
                                                />
                                                <Field.RequiredIndicator />
                                                {expandedTextTranslationKey && (
                                                    <>
                                                        {expandedItems[index] &&
                                                        expandedTextTranslationKey ? (
                                                            <Text
                                                                as="span"
                                                                fontSize="xs"
                                                                ml={1}
                                                                dangerouslySetInnerHTML={{
                                                                    __html: `${t(
                                                                        expandedTextTranslationKey,
                                                                        urls || {},
                                                                    )}`,
                                                                }}
                                                            />
                                                        ) : null}

                                                        <Button
                                                            variant="link"
                                                            ml={1}
                                                            size={'xs'}
                                                            onClick={(
                                                                e: React.MouseEvent<HTMLElement>,
                                                            ) =>
                                                                toggleExpandedTextVisibility(
                                                                    e,
                                                                    index,
                                                                )
                                                            }
                                                        >
                                                            {expandedItems[index]
                                                                ? translationData?.collapse
                                                                : translationData?.expand}
                                                        </Button>
                                                    </>
                                                )}
                                            </Checkbox>
                                            <Field.ErrorText
                                                data-testid={`error-message-consent-${id}`}
                                            >
                                                {invalid
                                                    ? t(validationMessage ?? 'required').toString()
                                                    : null}
                                            </Field.ErrorText>
                                        </Field.Root>
                                    )}
                                ></Controller>
                            );
                        },
                    )}
                </VStack>
                {!hideAdministratorData ? (
                    <VStack mt={3} fontSize={'xs'} align="stretch">
                        <Text>{translationData?.administratorInfo}</Text>
                        <Show when={showAdministratorData}>
                            <Box
                                dangerouslySetInnerHTML={{
                                    __html: translationData?.administratorInfoExpanded,
                                }}
                            />
                        </Show>

                        <Box textAlign={'right'}>
                            <Button
                                variant="link"
                                fontSize={'xs'}
                                onClick={() => setShowAdministratorData(!showAdministratorData)}
                            >
                                {showAdministratorData
                                    ? translationData?.collapse
                                    : translationData?.expand}
                            </Button>
                        </Box>
                    </VStack>
                ) : null}
            </Box>
        );
    },
);
